import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';

/**
 * Native Cloudflare Stream video player using HLS.js.
 * Optimized for smooth playback at the highest available quality.
 */
const StreamPlayer = ({
    videoUrl,
    autoPlay = false,
    controls = true,
    className = '',
    style = {},
    loop = false,
    muted,
    poster,
    onLoadedMetadata,
}) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const mountedRef = useRef(true);
    const [useFallback, setUseFallback] = useState(false);

    const isMuted = muted !== undefined ? muted : autoPlay;
    const manifestUrl = `https://${videoUrl}/manifest/video.m3u8`;

    const initPlayer = useCallback(() => {
        const video = videoRef.current;
        if (!video || !mountedRef.current) return;

        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        if (Hls.isSupported()) {
            const hls = new Hls({
                // Large buffers for uninterrupted playback
                maxBufferLength: 60,
                maxMaxBufferLength: 120,
                maxBufferSize: 60 * 1000 * 1000, // 60 MB
                maxBufferHole: 0.5,

                // Force highest quality from the start
                startLevel: -1,
                capLevelToPlayerSize: false,
                
                // High bandwidth estimate so ABR never downgrades
                abrEwmaDefaultEstimate: 50000000, // 50 Mbps
                abrBandWidthFactor: 0.95,
                abrBandWidthUpFactor: 0.7,
                
                // Enable web workers for better performance
                enableWorker: true,
                lowLatencyMode: false,

                // Pre-load ahead of playback position
                backBufferLength: 30,

                // Retry on errors
                manifestLoadingMaxRetry: 4,
                levelLoadingMaxRetry: 4,
                fragLoadingMaxRetry: 6,
            });

            hls.loadSource(manifestUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                if (!mountedRef.current) return;

                // Force and lock to highest quality level
                if (data.levels && data.levels.length > 0) {
                    const highest = data.levels.length - 1;
                    hls.currentLevel = highest;
                    hls.loadLevel = highest;
                }

                if (autoPlay) {
                    video.play().catch(() => {
                        video.muted = true;
                        video.play().catch(() => {});
                    });
                }
            });

            // Prevent ABR from switching to a lower level
            hls.on(Hls.Events.LEVEL_SWITCHING, (event, data) => {
                if (hls.levels && hls.levels.length > 0) {
                    const highest = hls.levels.length - 1;
                    if (data.level !== highest) {
                        hls.currentLevel = highest;
                    }
                }
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    hls.destroy();
                    hlsRef.current = null;
                    if (mountedRef.current) setUseFallback(true);
                }
            });

            hlsRef.current = hls;
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = manifestUrl;
            video.addEventListener('error', () => {
                if (mountedRef.current) setUseFallback(true);
            });
            if (autoPlay) {
                video.play().catch(() => {
                    video.muted = true;
                    video.play().catch(() => {});
                });
            }
        } else {
            setUseFallback(true);
        }
    }, [manifestUrl, autoPlay]);

    useEffect(() => {
        mountedRef.current = true;
        if (!useFallback) initPlayer();

        return () => {
            mountedRef.current = false;
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [initPlayer, useFallback]);

    if (useFallback) {
        return (
            <iframe
                src={`https://${videoUrl}/iframe?${autoPlay ? 'autoplay=true&' : ''}preload=true`}
                className={className}
                style={{ border: 'none', ...style }}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                title="Video player"
            />
        );
    }

    return (
        <video
            ref={videoRef}
            controls={controls}
            loop={loop}
            muted={isMuted}
            playsInline
            preload="auto"
            poster={poster}
            onLoadedMetadata={onLoadedMetadata}
            className={className}
            style={style}
        />
    );
};

export default StreamPlayer;
